import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import QuestionnaireSignupForm from '../components/portal/QuestionnaireSignupForm';
import LoadingScreen from '../components/ui/LoadingScreen';
import {
  createPublicQuestionnaireLead,
  parsePendingLeadParams,
} from '../utils/publicQuestionnaireLead';

const QuestionnaireLandingPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [error, setError] = useState<string | null>(null);
  const [autoStarting, setAutoStarting] = useState(false);
  const autoStartedRef = useRef(false);

  const parsedParams = useMemo(
    () => parsePendingLeadParams(searchParams.toString()),
    [searchParams]
  );

  const partialValues = useMemo(() => {
    if (parsedParams) return parsedParams;
    return {
      firstName: searchParams.get('fn') || undefined,
      lastName: searchParams.get('ln') || undefined,
      email: searchParams.get('email') || undefined,
      location: searchParams.get('loc') || searchParams.get('location') || undefined,
    };
  }, [parsedParams, searchParams]);

  useEffect(() => {
    if (!parsedParams || autoStartedRef.current) return;

    autoStartedRef.current = true;
    setAutoStarting(true);

    createPublicQuestionnaireLead(parsedParams)
      .then((result) => {
        if (result.ok) {
          navigate(`/portal/${result.slug}/questionnaire`, { replace: true });
          return;
        }
        setError(result.error);
        setAutoStarting(false);
      })
      .catch(() => {
        setError('Could not start your questionnaire. Please try again.');
        setAutoStarting(false);
      });
  }, [navigate, parsedParams]);

  if (autoStarting) {
    return <LoadingScreen message="Loading your style guide..." size="medium" />;
  }

  if (error) {
    return (
      <QuestionnaireSignupForm
        initialValues={partialValues}
        onSuccess={({ slug }) => {
          navigate(`/portal/${slug}/questionnaire`, { replace: true });
        }}
      />
    );
  }

  return (
    <QuestionnaireSignupForm
      initialValues={partialValues}
      onSuccess={({ slug }) => {
        navigate(`/portal/${slug}/questionnaire`, { replace: true });
      }}
    />
  );
};

export default QuestionnaireLandingPage;
